import { Injectable } from "@angular/core";
import { Item } from "src/app/inspection/model/item";
import { HttpService } from "src/app/session/services/http.service";
import { environment } from "src/environments/environment";
import { FormFieldChange } from "../components/item-form/dynamic-form-actions-base";
import { CommandHandler } from "../../shared/ddd/contracts/command-handler.interface";
import { QueryHandler } from "src/app/shared/ddd/contracts/query-handler.interface";
import { InvokeResult } from "src/app/shared/ddd/models/invoke-result";
import { Query } from "src/app/shared/ddd/models/query";
import { Command } from "src/app/shared/ddd/models/command";
import { ItemFieldChangeCommand } from "./commands/items-field-change.command";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ItFieldInfo } from "src/app/inspection/model/it-field-info";
import { ItemFormQuery } from "./queries/item-form.query";
import { DynamicFormData } from "src/app/inspection/model/dynamic-form-data";
import { CotecnaUserService } from "@cotecna/session";
import { AddItemsToInspectionCommand } from "./commands/add-items-to-inspection.command";
import { ItemLight } from "src/app/inspection/model/itemLight";
import { ItemsLightQuery } from "./queries/items.light.query";

@Injectable({
  providedIn: "root",
})
export class ItemsService implements QueryHandler, CommandHandler {
  private _items = new Subject<Item[]>();
  private _itemsLight = new Subject<ItemLight[]>();
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _itemFormData = new Subject<DynamicFormData>();

  public get items$(): Observable<Item[]> {
    return this._items.asObservable();
  }

  public get itemsLight$(): Observable<ItemLight[]> {
    return this._itemsLight.asObservable();
  }

  public get isLoadingItems$(): Observable<boolean> {
    return this._isLoading.asObservable();
  }

  public get itemFormData(): Observable<DynamicFormData> {
    return this._itemFormData;
  }

  constructor(
    private httpService: HttpService,
    private userService: CotecnaUserService,
  ) {}

  public invokeQuery<TQuery extends Query>(
    query: TQuery,
  ): Promise<InvokeResult> {
    switch (query.constructor) {
      case ItemsLightQuery:
        return this.invokeItemsLight(query);
      case ItemFormQuery:
        return this.invokeItemForm(query);
      default:
        throw new Error("Query not allowed");
    }
  }

  public invokeCommand<TCommand extends Command>(
    command: TCommand,
  ): Promise<InvokeResult> {
    switch (command.constructor) {
      case ItemFieldChangeCommand:
        return this.invokeItemFieldChange(command);
      case AddItemsToInspectionCommand:
        return this.invokeAddItemToInspection(command);
      default:
        throw new Error("Command not allowed");
    }
  }

  public async applyItemFieldChange(
    formValues: ItFieldInfo[],
    formFieldChange: FormFieldChange,
  ) {
    const fieldInfo = formValues.find(
      (fieldValue) => fieldValue.fieldId === formFieldChange.fieldId,
    );
    if (fieldInfo) {
      fieldInfo.value = formFieldChange.value;
      fieldInfo.modificationDate = formFieldChange.timestamp;
    } else {
      formValues.push({
        fieldId: formFieldChange.fieldId,
        value: formFieldChange.value,
        modificationDate: formFieldChange.timestamp,
        modifiedBy: this.userService.getUser().name,
      });
    }
  }

  public async editItem(inspectionId: string, item: Item): Promise<Item> {
    const itemEdited = { ...item, inspectionId, itemId: item.id };
    await this.httpService
      .put(`${environment.webApiUrl}/api/item`, itemEdited)
      .toPromise();
    return itemEdited;
  }

  private async invokeItemsLight<TQuery extends Query>(
    query: TQuery,
  ): Promise<InvokeResult> {
    this._isLoading.next(true);
    const items: ItemLight[] = await this.getInspectionItemsLight(query);
    this._itemsLight.next(items);
    this._isLoading.next(false);
    return {
      result: true,
      message: "get inspection items light - OK",
    } as InvokeResult;
  }

  private async invokeItemForm<TQuery extends Query>(
    query: TQuery,
  ): Promise<InvokeResult> {
    const itemFormData: DynamicFormData = await this.getItemFormData(query);
    itemFormData.inspectionId = (query as any).inspectionId;
    itemFormData.itemId = (query as any).itemId;
    this._itemFormData.next(itemFormData);
    return { result: true, message: "get item form data - OK" } as InvokeResult;
  }

  private async invokeItemFieldChange<TCommand extends Command>(
    command: TCommand,
  ): Promise<InvokeResult> {
    await this.updateItemFieldChangeToNetwork(command);
    return {
      result: true,
      message: "update item field change - OK",
    } as InvokeResult;
  }

  private async invokeAddItemToInspection<TCommand extends Command>(
    command: TCommand,
  ): Promise<InvokeResult> {
    await this.performAddItemToInspectionApiCall(<any>command);
    return { result: true, message: "Add item - OK" } as InvokeResult;
  }

  async getInspectionItemsLight(query: any): Promise<ItemLight[]> {
    const url = `${environment.webApiUrl}/api/inspectionDetails/itemList-light/${query.inspectionId}`;
    return this.httpService.get(url).toPromise();
  }

  private async getItemFormData(query: any): Promise<DynamicFormData> {
    const url = `${environment.webApiUrl}/api/inspectionDetails/itemForm?inspectionId=${query.inspectionId}&itemId=${query.itemId}`;
    return this.httpService.get(url).toPromise();
  }

  private async updateItemFieldChangeToNetwork(command: any): Promise<boolean> {
    return this.httpService
      .put(
        `${environment.webApiUrl}/api/inspection/updateItemsFormWeb`,
        { fieldChanges: [command.fieldChange] },
        { withCredentials: true },
      )
      .toPromise();
  }

  private async performAddItemToInspectionApiCall(
    command: AddItemsToInspectionCommand,
  ): Promise<boolean> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/inspection/${command.inspectionId}/add-items`,
        command.items,
        { withCredentials: true },
      )
      .toPromise();
  }

  async removeItem(inspectionId: string, itemId: string): Promise<any> {
    const params = { inspectionID: inspectionId, itemID: itemId };
    return this.httpService
      .put(`${environment.webApiUrl}/api/inspection/removeItem`, params, {
        withCredentials: true,
      })
      .toPromise();
  }
}
